import { apiRequest } from './api';
import { t, onLocaleChange } from './i18n';

// ============================================================
// Плашка «Сейчас в эфире» на всех страницах.
// Данные о live-статусе считает сервер (/api/streamers) через Twitch API;
// здесь только периодический опрос и рендер плашки, когда кто-то стримит.
// ============================================================

interface LiveInfo {
    title: string;
    game: string;
    viewers: number;
    thumbnail: string;
    started_at: string;
}

interface StreamerItem {
    id: number;
    name: string;
    url: string;
    login: string | null;
    /** Игрок, к которому привязан канал (раздел «Ссылки» в карточке). */
    player_id: number | null;
    is_live: boolean;
    off_topic?: boolean;
    live: LiveInfo | null;
}

interface StreamersResponse {
    streamers: StreamerItem[];
    live_enabled: boolean;
}

const POLL_MS = 60_000;
/** Звук при старте стрима временно выключен (код сигнала оставлен). */
const LIVE_SOUND_ENABLED = false;

let items: StreamerItem[] = [];
let dismissed = false;
let started = false;
let timer: ReturnType<typeof setInterval> | null = null;
/** id каналов, которые были в эфире при прошлом опросе. */
let knownLive = new Set<number>();
let hasLoaded = false;

/** WebAudio-контекст создаём лениво и «прогреваем» на первом действии пользователя. */
let audioCtx: AudioContext | null = null;

export function setupStreamsBanner(): void {
    if (started) return;
    started = true;

    document.getElementById('streams-banner-close')?.addEventListener('click', () => {
        dismissed = true;
        document.getElementById('streams-banner')?.classList.add('hidden');
    });

    // Браузеры не дают играть звук без жеста — подготавливаем контекст заранее.
    if (LIVE_SOUND_ENABLED) {
        const prime = (): void => {
            const ctx = ensureAudioContext();
            if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
        };
        document.addEventListener('pointerdown', prime, { passive: true });
        document.addEventListener('keydown', prime);
    }

    onLocaleChange(() => render());

    void load();
    timer = setInterval(() => void load(), POLL_MS);
}

async function load(): Promise<void> {
    try {
        const res = await apiRequest<StreamersResponse>('/api/streamers');
        items = res.streamers ?? [];

        const nextLive = new Set(items.filter((s) => s.is_live).map((s) => s.id));
        if (hasLoaded) {
            const appeared = [...nextLive].some((id) => !knownLive.has(id));
            if (appeared) {
                // Новый стрим снова показываем, даже если плашку закрывали.
                dismissed = false;
                if (LIVE_SOUND_ENABLED) playLiveSound();
            }
        }
        knownLive = nextLive;
        hasLoaded = true;

        render();
    } catch (err) {
        console.warn('[streams] load error:', err);
    }
}

function ensureAudioContext(): AudioContext | null {
    if (audioCtx) return audioCtx;
    const Ctor = window.AudioContext
        ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    try {
        audioCtx = new Ctor();
    } catch {
        return null;
    }
    return audioCtx;
}

/** Короткий трёхнотный сигнал «кто-то вышел в эфир». */
function playLiveSound(): void {
    const ctx = ensureAudioContext();
    if (!ctx) return;

    const chime = (): void => {
        const now = ctx.currentTime;
        [880, 1174.66, 1567.98].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            const start = now + i * 0.12;
            osc.type = 'sine';
            osc.frequency.value = freq;
            gain.gain.setValueAtTime(0.0001, start);
            gain.gain.exponentialRampToValueAtTime(0.22, start + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.28);
            osc.connect(gain).connect(ctx.destination);
            osc.start(start);
            osc.stop(start + 0.3);
        });
    };

    if (ctx.state === 'suspended') {
        void ctx.resume().then(chime).catch(() => undefined);
        return;
    }
    chime();
}

function render(): void {
    const banner = document.getElementById('streams-banner');
    const list = document.getElementById('streams-banner-list');
    if (!banner || !list) return;

    const live = items.filter((s) => s.is_live);
    if (live.length === 0 || dismissed) {
        banner.classList.add('hidden');
        return;
    }

    // Нет ни одного стрима именно в категории StarCraft II — вся плашка серая.
    banner.classList.toggle(
        'streams-banner--muted',
        live.every((s) => s.off_topic)
    );

    list.innerHTML = '';
    for (const s of live) {
        const a = document.createElement('a');
        a.className = 'streams-banner-item';
        // Не StarCraft II, но в названии FFA — показываем нормально,
        // только сама плашка приглушённая и с пометкой.
        if (s.off_topic) a.classList.add('streams-banner-item--potential');
        a.href = s.url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';

        const dot = document.createElement('span');
        dot.className = 'streams-banner-dot';

        const name = document.createElement('span');
        name.className = 'streams-banner-name';
        name.textContent = s.name;

        a.append(dot, name);

        // Название стрима показываем всегда, а не только в tooltip.
        if (s.live?.title) {
            const title = document.createElement('span');
            title.className = 'streams-banner-title';
            title.textContent = s.live.title;
            a.appendChild(title);
        }

        if (s.off_topic) {
            const tag = document.createElement('span');
            tag.className = 'streams-banner-tag';
            tag.textContent = t('streams.ffa_candidate');
            a.appendChild(tag);
        }

        if (s.live && s.live.viewers > 0) {
            const viewers = document.createElement('span');
            viewers.className = 'streams-banner-viewers';
            viewers.textContent = t('streams.viewers', { n: formatViews(s.live.viewers) });
            a.appendChild(viewers);
        }

        list.appendChild(a);
    }

    banner.classList.remove('hidden');
}

function formatViews(n: number): string {
    if (n >= 1000) return `${(n / 1000).toFixed(n >= 10_000 ? 0 : 1)}K`;
    return String(n);
}

/** На случай будущего явного сброса (сейчас не вызывается). */
export function stopStreamsBanner(): void {
    if (timer) {
        clearInterval(timer);
        timer = null;
    }
    started = false;
}
